import { useEffect, useRef, useState } from "react";

type Props = {
  targetNumber: number;
  duration?: number;
};

const CountUpNumber = ({ targetNumber, duration = 2000 }: Props) => {
  const [currentNumber, setCurrentNumber] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const elementRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isInView) return;

    const startTime = performance.now();

    const step = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const value = Math.floor(progress * targetNumber);
      setCurrentNumber(value);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [isInView, targetNumber, duration]);

  const formattedNumber = currentNumber.toLocaleString("en-US");

  return <span ref={elementRef}>{formattedNumber}</span>;
};

export default CountUpNumber;
